import {
  deleteCategory,
  deleteNews,
  deleteOrder,
  deleteWork,
  loadCategories,
  loadNews,
  loadOrders,
  loadSite,
  loadWorks,
  login,
  logout,
  me,
  saveCategory,
  saveNews,
  saveSite,
  saveWork,
  uploadFile,
  url,
} from "./api";
import { categoryName, escapeHtml, mediaSrc } from "./render";
import type { Category, Media, NewsItem, Site, Work } from "./types";

type Tab = "obras" | "novedades" | "categorias" | "pedidos" | "sitio";

const emptyWork = (): Work => ({
  id: "",
  title: "",
  slug: "",
  category: "",
  description: "",
  media: [],
  featured: false,
  featuredOrder: 0,
  createdAt: new Date().toISOString().slice(0, 10),
});

const emptyNews = (): NewsItem => ({
  id: "",
  title: "",
  slug: "",
  body: "",
  media: [],
  createdAt: new Date().toISOString().slice(0, 10),
  published: true,
});

export async function initAdmin(): Promise<void> {
  const root = document.getElementById("admin-root");
  if (!root) return;
  const session = await me();
  if (!session.auth) {
    renderLogin(root, session.setup);
    return;
  }
  await renderApp(root);
}

function renderLogin(root: HTMLElement, setup: boolean): void {
  root.innerHTML = `
    <main class="wrap login-box">
      <p class="kicker">Taller</p>
      <h1>${setup ? "Creá tu clave" : "Entrar"}</h1>
      <p class="lede">${setup ? "La primera vez elegís una contraseña de al menos 8 caracteres. Guardala." : "Solo Norma."}</p>
      <form class="form" id="login-form">
        <div id="login-flash"></div>
        <label>Contraseña <input name="password" type="password" required minlength="8" autocomplete="current-password" /></label>
        <button class="btn" type="submit">${setup ? "Guardar y entrar" : "Entrar"}</button>
        <p class="form-note"><a href="${url("index.html")}">Volver al sitio</a></p>
      </form>
    </main>
  `;
  const form = document.getElementById("login-form") as HTMLFormElement;
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const password = String(new FormData(form).get("password") ?? "");
    const flash = document.getElementById("login-flash");
    const result = await login(password);
    if (!result.ok) {
      if (flash) flash.innerHTML = `<p class="flash err">${escapeHtml(result.error || "No se pudo entrar.")}</p>`;
      return;
    }
    await initAdmin();
  });
}

async function renderApp(root: HTMLElement): Promise<void> {
  let tab: Tab = "obras";
  const [works, news, cats, site] = await Promise.all([
    loadWorks(),
    loadNews(),
    loadCategories(),
    loadSite(),
  ]);
  let state = { works, news, cats, site, orders: [] as Awaited<ReturnType<typeof loadOrders>> };

  const paint = async (): Promise<void> => {
    root.innerHTML = `
      <div class="admin-shell">
        <aside class="admin-side">
          <strong>Taller</strong>
          <a href="#obras" data-tab="obras" ${tab === "obras" ? 'aria-current="true"' : ""}>Obras</a>
          <a href="#novedades" data-tab="novedades" ${tab === "novedades" ? 'aria-current="true"' : ""}>Novedades</a>
          <a href="#categorias" data-tab="categorias" ${tab === "categorias" ? 'aria-current="true"' : ""}>Categorías</a>
          <a href="#pedidos" data-tab="pedidos" ${tab === "pedidos" ? 'aria-current="true"' : ""}>Pedidos</a>
          <a href="#sitio" data-tab="sitio" ${tab === "sitio" ? 'aria-current="true"' : ""}>El sitio</a>
          <button class="linkish" type="button" id="salir">Salir</button>
          <a href="${url("index.html")}">Ver la web</a>
        </aside>
        <main class="admin-main" id="panel"></main>
      </div>
    `;
    document.getElementById("salir")?.addEventListener("click", async () => {
      await logout();
      await initAdmin();
    });
    root.querySelectorAll<HTMLAnchorElement>("[data-tab]").forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        tab = (link.dataset.tab as Tab) || "obras";
        void paint();
      });
    });
    const panel = document.getElementById("panel");
    if (!panel) return;
    if (tab === "obras") renderWorks(panel, state, paint);
    if (tab === "novedades") renderNews(panel, state, paint);
    if (tab === "categorias") renderCats(panel, state, paint);
    if (tab === "pedidos") {
      state.orders = await loadOrders();
      renderOrders(panel, state, paint);
    }
    if (tab === "sitio") renderSite(panel, state);
  };

  await paint();
}

function flash(host: HTMLElement, message: string, ok = true): void {
  const box = host.querySelector("#admin-flash") || host;
  const html = `<p class="flash ${ok ? "ok" : "err"}">${escapeHtml(message)}</p>`;
  if (box.id === "admin-flash") box.innerHTML = html;
  else host.insertAdjacentHTML("afterbegin", `<div id="admin-flash">${html}</div>`);
}

function mediaPicker(list: Media[], onChange: (next: Media[]) => void): HTMLElement {
  const wrap = document.createElement("div");
  const draw = (): void => {
    wrap.innerHTML = `
      <p>Fotos y videos</p>
      <div class="media-list">
        ${list
          .map((item, index) => {
            const src = mediaSrc(item.src);
            const thumb =
              item.type === "image"
                ? `<img src="${escapeHtml(src)}" alt="" />`
                : item.type === "video"
                  ? `<video src="${escapeHtml(src)}"></video>`
                  : `<small>YouTube</small>`;
            return `<figure data-i="${index}">${thumb}<button type="button" aria-label="Quitar">×</button></figure>`;
          })
          .join("")}
      </div>
      <div class="row">
        <label class="btn btn-ghost">Subir archivo<input type="file" accept="image/*,video/mp4,video/webm" hidden /></label>
        <input type="url" placeholder="O un enlace de YouTube" id="yt" />
        <button class="btn btn-ghost" type="button" id="add-yt">Agregar</button>
      </div>
    `;
    wrap.querySelectorAll("figure button").forEach((btn) => {
      btn.addEventListener("click", () => {
        const i = Number((btn.parentElement as HTMLElement).dataset.i);
        list.splice(i, 1);
        onChange(list);
        draw();
      });
    });
    const file = wrap.querySelector<HTMLInputElement>('input[type="file"]');
    file?.addEventListener("change", async () => {
      const chosen = file.files?.[0];
      if (!chosen) return;
      const result = await uploadFile(chosen);
      if (!result.ok || !result.data) {
        alert(result.error || "No se pudo subir.");
        return;
      }
      list.push(result.data);
      onChange(list);
      draw();
    });
    wrap.querySelector("#add-yt")?.addEventListener("click", () => {
      const input = wrap.querySelector<HTMLInputElement>("#yt");
      const value = input?.value.trim() ?? "";
      if (!value) return;
      list.push({ type: "youtube", src: value, alt: "" });
      onChange(list);
      if (input) input.value = "";
      draw();
    });
  };
  draw();
  return wrap;
}

function editor(initial: string, onInput: (html: string) => void): HTMLElement {
  const wrap = document.createElement("div");
  wrap.innerHTML = `
    <div class="editor-toolbar">
      <button type="button" data-cmd="bold"><b>N</b></button>
      <button type="button" data-cmd="italic"><i>C</i></button>
      <button type="button" data-cmd="insertUnorderedList">Lista</button>
      <button type="button" data-cmd="formatBlock" data-val="h3">Título</button>
    </div>
    <div class="editor" contenteditable="true">${initial}</div>
  `;
  const area = wrap.querySelector<HTMLElement>(".editor")!;
  wrap.querySelectorAll<HTMLButtonElement>("[data-cmd]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const cmd = btn.dataset.cmd || "bold";
      const val = btn.dataset.val;
      document.execCommand(cmd, false, val);
      area.focus();
      onInput(area.innerHTML);
    });
  });
  area.addEventListener("input", () => onInput(area.innerHTML));
  return wrap;
}

function renderWorks(
  panel: HTMLElement,
  state: { works: Work[]; cats: Category[] },
  paint: () => Promise<void>
): void {
  let draft = emptyWork();
  const mount = (): void => {
    panel.innerHTML = `
      <h1>${draft.id ? "Editar obra" : "Nueva obra"}</h1>
      <div id="admin-flash"></div>
      <div class="admin-card" id="work-form"></div>
      <h2>Todas</h2>
      <table class="table">
        <thead><tr><th>Pieza</th><th>Categoría</th><th>Inicio</th><th></th></tr></thead>
        <tbody>
          ${state.works
            .map(
              (work) => `<tr>
                <td>${escapeHtml(work.title)}</td>
                <td>${escapeHtml(categoryName(work.category, state.cats))}</td>
                <td>${work.featured ? `sí (${work.featuredOrder})` : "no"}</td>
                <td class="row">
                  <button class="btn btn-ghost" data-edit="${work.id}" type="button">Editar</button>
                  <button class="btn btn-ghost" data-del="${work.id}" type="button">Borrar</button>
                </td>
              </tr>`
            )
            .join("")}
        </tbody>
      </table>
    `;
    const form = document.getElementById("work-form")!;
    form.innerHTML = `
      <label>Título <input id="w-title" value="${escapeHtml(draft.title)}" /></label>
      <label>Categoría
        <select id="w-cat">
          ${state.cats.map((c) => `<option value="${c.id}" ${c.id === draft.category ? "selected" : ""}>${escapeHtml(c.name)}</option>`).join("")}
        </select>
      </label>
      <label>Fecha <input id="w-date" type="date" value="${escapeHtml(draft.createdAt)}" /></label>
      <label class="check"><input id="w-feat" type="checkbox" ${draft.featured ? "checked" : ""} /> Mostrar en la página de inicio</label>
      <label>Orden en el inicio <input id="w-ord" type="number" value="${draft.featuredOrder}" /></label>
      <p>Texto</p>
    `;
    const ed = editor(draft.description, (html) => {
      draft.description = html;
    });
    form.appendChild(ed);
    form.appendChild(
      mediaPicker(draft.media, (next) => {
        draft.media = next;
      })
    );
    const actions = document.createElement("div");
    actions.className = "row";
    actions.style.marginTop = "1rem";
    actions.innerHTML = `
      <button class="btn" type="button" id="w-save">Guardar</button>
      <button class="btn btn-ghost" type="button" id="w-new">Nueva</button>
    `;
    form.appendChild(actions);

    document.getElementById("w-save")?.addEventListener("click", async () => {
      draft.title = (document.getElementById("w-title") as HTMLInputElement).value.trim();
      draft.category = (document.getElementById("w-cat") as HTMLSelectElement).value;
      draft.createdAt = (document.getElementById("w-date") as HTMLInputElement).value;
      draft.featured = (document.getElementById("w-feat") as HTMLInputElement).checked;
      draft.featuredOrder = Number((document.getElementById("w-ord") as HTMLInputElement).value || 0);
      const result = await saveWork(draft);
      if (!result.ok) {
        flash(panel, result.error || "No se pudo guardar.", false);
        return;
      }
      state.works = result.data ?? state.works;
      draft = emptyWork();
      mount();
      flash(panel, "Obra guardada.");
    });
    document.getElementById("w-new")?.addEventListener("click", () => {
      draft = emptyWork();
      mount();
    });
    panel.querySelectorAll<HTMLButtonElement>("[data-edit]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const found = state.works.find((w) => w.id === btn.dataset.edit);
        if (found) {
          draft = { ...found, media: [...found.media] };
          mount();
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      });
    });
    panel.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        if (!confirm("¿Borrar esta obra?")) return;
        const result = await deleteWork(btn.dataset.del || "");
        if (result.ok) {
          state.works = result.data ?? state.works;
          mount();
        }
      });
    });
  };
  mount();
  void paint;
}

function renderNews(
  panel: HTMLElement,
  state: { news: NewsItem[] },
  paint: () => Promise<void>
): void {
  let draft = emptyNews();
  const mount = (): void => {
    panel.innerHTML = `
      <h1>${draft.id ? "Editar novedad" : "Nueva novedad"}</h1>
      <div id="admin-flash"></div>
      <div class="admin-card" id="news-form"></div>
      <h2>Publicadas</h2>
      <table class="table">
        <thead><tr><th>Título</th><th>Fecha</th><th></th></tr></thead>
        <tbody>
          ${state.news
            .map(
              (item) => `<tr>
                <td>${escapeHtml(item.title)}</td>
                <td>${escapeHtml(item.createdAt)}</td>
                <td class="row">
                  <button class="btn btn-ghost" data-edit="${item.id}" type="button">Editar</button>
                  <button class="btn btn-ghost" data-del="${item.id}" type="button">Borrar</button>
                </td>
              </tr>`
            )
            .join("")}
        </tbody>
      </table>
    `;
    const form = document.getElementById("news-form")!;
    form.innerHTML = `
      <label>Título <input id="n-title" value="${escapeHtml(draft.title)}" /></label>
      <label>Fecha <input id="n-date" type="date" value="${escapeHtml(draft.createdAt)}" /></label>
      <label class="check"><input id="n-pub" type="checkbox" ${draft.published ? "checked" : ""} /> Visible en Novedades</label>
      <p>Texto</p>
    `;
    form.appendChild(editor(draft.body, (html) => {
      draft.body = html;
    }));
    form.appendChild(
      mediaPicker(draft.media, (next) => {
        draft.media = next;
      })
    );
    const actions = document.createElement("div");
    actions.className = "row";
    actions.style.marginTop = "1rem";
    actions.innerHTML = `<button class="btn" type="button" id="n-save">Guardar</button>
      <button class="btn btn-ghost" type="button" id="n-new">Nueva</button>`;
    form.appendChild(actions);

    document.getElementById("n-save")?.addEventListener("click", async () => {
      draft.title = (document.getElementById("n-title") as HTMLInputElement).value.trim();
      draft.createdAt = (document.getElementById("n-date") as HTMLInputElement).value;
      draft.published = (document.getElementById("n-pub") as HTMLInputElement).checked;
      const result = await saveNews(draft);
      if (!result.ok) {
        flash(panel, result.error || "No se pudo guardar.", false);
        return;
      }
      state.news = result.data ?? state.news;
      draft = emptyNews();
      mount();
      flash(panel, "Novedad guardada.");
    });
    document.getElementById("n-new")?.addEventListener("click", () => {
      draft = emptyNews();
      mount();
    });
    panel.querySelectorAll<HTMLButtonElement>("[data-edit]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const found = state.news.find((n) => n.id === btn.dataset.edit);
        if (found) {
          draft = { ...found, media: [...found.media] };
          mount();
        }
      });
    });
    panel.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        if (!confirm("¿Borrar esta novedad?")) return;
        const result = await deleteNews(btn.dataset.del || "");
        if (result.ok) {
          state.news = result.data ?? state.news;
          mount();
        }
      });
    });
  };
  mount();
  void paint;
}

function renderCats(
  panel: HTMLElement,
  state: { cats: Category[] },
  paint: () => Promise<void>
): void {
  panel.innerHTML = `
    <h1>Categorías</h1>
    <div id="admin-flash"></div>
    <div class="admin-card row">
      <input id="c-name" placeholder="Nombre, por ejemplo Joyería" />
      <button class="btn" type="button" id="c-save">Agregar</button>
    </div>
    <table class="table">
      ${state.cats
        .map(
          (c) => `<tr>
            <td>${escapeHtml(c.name)}</td>
            <td><button class="btn btn-ghost" data-del="${c.id}" type="button">Borrar</button></td>
          </tr>`
        )
        .join("")}
    </table>
  `;
  document.getElementById("c-save")?.addEventListener("click", async () => {
    const name = (document.getElementById("c-name") as HTMLInputElement).value.trim();
    if (!name) return;
    const result = await saveCategory({ id: "", name, slug: "" });
    if (!result.ok) {
      flash(panel, result.error || "No se pudo guardar.", false);
      return;
    }
    state.cats = result.data ?? state.cats;
    await paint();
  });
  panel.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      if (!confirm("¿Borrar esta categoría?")) return;
      const result = await deleteCategory(btn.dataset.del || "");
      if (result.ok) {
        state.cats = result.data ?? state.cats;
        await paint();
      }
    });
  });
}

function orderWorkLabel(obra: string, works: Work[]): string {
  if (!obra) return "sin pieza de referencia";
  const titles = new Map(works.map((work) => [work.id, work.title]));
  return obra
    .split(/,\s*/)
    .map((id) => titles.get(id) || id)
    .join(", ");
}

function renderOrders(
  panel: HTMLElement,
  state: { orders: Awaited<ReturnType<typeof loadOrders>>; works: Work[] },
  paint: () => Promise<void>
): void {
  panel.innerHTML = `
    <h1>Pedidos</h1>
    <p class="form-note">También llegan al mail configurado en <code>api/config.php</code>.</p>
    ${
      state.orders.length === 0
        ? `<p class="empty">Todavía no hay encargos.</p>`
        : state.orders
            .map(
              (order) => `<article class="admin-card">
                <p><strong>${escapeHtml(order.nombre)}</strong> · ${escapeHtml(order.email)} · ${escapeHtml(order.telefono || "—")}</p>
                <p class="form-note">${escapeHtml(order.createdAt)} · ${escapeHtml(order.tipo)} · ${escapeHtml(orderWorkLabel(order.obra, state.works))}</p>
                <p>${escapeHtml(order.medidas)}</p>
                <p>${escapeHtml(order.mensaje)}</p>
                <button class="btn btn-ghost" data-del="${order.id}" type="button">Borrar</button>
              </article>`
            )
            .join("")
    }
  `;
  panel.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      if (!confirm("¿Borrar este pedido?")) return;
      const result = await deleteOrder(btn.dataset.del || "");
      if (result.ok) {
        state.orders = result.data ?? [];
        await paint();
      }
    });
  });
}

function renderSite(panel: HTMLElement, state: { site: Site }): void {
  const s = state.site;
  panel.innerHTML = `
    <h1>El sitio</h1>
    <div id="admin-flash"></div>
    <div class="admin-card form" id="site-form">
      <label>Nombre <input id="s-name" value="${escapeHtml(s.artistName)}" /></label>
      <label>Línea corta <input id="s-tag" value="${escapeHtml(s.tagline)}" /></label>
      <label>Título de inicio <input id="s-hero" value="${escapeHtml(s.heroTitle)}" /></label>
      <label>Texto de inicio <textarea id="s-herot">${escapeHtml(s.heroText)}</textarea></label>
      <label>Intro <textarea id="s-intro">${escapeHtml(s.intro)}</textarea></label>
      <p>Biografía</p>
      <div id="s-bio"></div>
      <label>Email de contacto (público) <input id="s-email" value="${escapeHtml(s.email)}" /></label>
      <label>WhatsApp (solo números, con código de país) <input id="s-wa" value="${escapeHtml(s.whatsapp)}" /></label>
      <label>Instagram <input id="s-ig" value="${escapeHtml(s.instagram)}" placeholder="usuario" /></label>
      <button class="btn" type="button" id="s-save">Guardar</button>
    </div>
  `;
  const bioHost = document.getElementById("s-bio")!;
  let bio = s.bio;
  bioHost.appendChild(editor(bio, (html) => {
    bio = html;
  }));
  document.getElementById("s-save")?.addEventListener("click", async () => {
    const next: Site = {
      artistName: (document.getElementById("s-name") as HTMLInputElement).value,
      tagline: (document.getElementById("s-tag") as HTMLInputElement).value,
      heroTitle: (document.getElementById("s-hero") as HTMLInputElement).value,
      heroText: (document.getElementById("s-herot") as HTMLTextAreaElement).value,
      intro: (document.getElementById("s-intro") as HTMLTextAreaElement).value,
      bio,
      email: (document.getElementById("s-email") as HTMLInputElement).value,
      whatsapp: (document.getElementById("s-wa") as HTMLInputElement).value,
      instagram: (document.getElementById("s-ig") as HTMLInputElement).value,
    };
    const result = await saveSite(next);
    if (!result.ok) {
      flash(panel, result.error || "No se pudo guardar.", false);
      return;
    }
    state.site = result.data ?? next;
    flash(panel, "Sitio actualizado.");
  });
}
